/**
 * Shared vocabulary for the recording-configuration UIs.
 *
 * The single-camera panel (RecordingConfigPanel) and the bulk editor
 * (BulkRecordingModeModal) offer the same settings, so their labels, hints and
 * timer-driven warnings live here rather than being written twice. Two copies drifted
 * once already — the bulk editor called modes "Event-based — keep footage around AI
 * detections" while the panel called them "Event-based / Keep footage around AI
 * detections", and only one of them knew that people-counting is timer-driven.
 *
 * FIELD_HINTS is the operator-facing explanation of what each setting actually does.
 * These are deliberately concrete ("stops 30s after the last detection") rather than
 * restating the label, because every one of them has generated a support question.
 */

export const MODE_LABELS: Record<string, { label: string; hint: string }> = {
  continuous: { label: 'Continuous', hint: 'Keep everything, 24/7' },
  event: { label: 'Event-based', hint: 'Keep footage around AI detections' },
  motion: { label: 'Motion-based', hint: 'Keep footage while the scene changes' },
  event_motion: { label: 'Event + Motion', hint: 'Either trigger keeps footage' },
};

/**
 * What a camera with no config row of its own does — the backend's CameraRecordingConfig
 * DEFAULTS. A missing row is fail-safe by design ("an unknown or unreadable config must never
 * cause footage to be discarded"), so it reads as continuous rather than off. Use this, not
 * null, once the config list has loaded: null means "still loading" to `recordingState`.
 */
export const IMPLICIT_RECORDING_CONFIG = { enabled: true, mode: 'continuous' };

/** What a camera's recording badge should say, and whether the "live" dot belongs on it. */
export interface RecordingState {
  /**
   * A subset of StatusTag's tones — the ones a recording badge can legitimately take.
   * `danger` is here for `recordingModeBadge`, which tints the whole chip when the camera
   * is unreachable; `recordingState` itself never returns it.
   */
  tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  label: string;
  /** True only when a pipeline is actually running, so the pulsing dot never lies. */
  live: boolean;
}

/**
 * Resolve the recording state shown on a camera summary (detail page header cards).
 *
 * Both dashboards used to answer this inline and got it wrong in different ways: the cloud
 * page hard-coded a pulsing dot and a green "Always On" pill for every camera, including ones
 * with recording switched off, and the local page treated "config hasn't loaded yet" as
 * enabled, so a disabled camera flashed a live recording dot on each page load.
 *
 * @param config  the camera's recording config; null/undefined means STILL LOADING, which is
 *                deliberately its own state rather than being folded into "on" or "off".
 * @param cameraOffline  outranks the mode. No pipeline runs for a camera that isn't
 *                connected, so reporting "Continuous" there describes the configuration
 *                rather than what is happening.
 */
export function recordingState(
  config: { enabled?: boolean; mode?: string } | null | undefined,
  cameraOffline = false,
): RecordingState {
  if (!config) return { label: 'Checking…', tone: 'neutral', live: false };
  if (config.enabled === false) return { label: 'Off', tone: 'neutral', live: false };
  if (cameraOffline) return { label: 'Camera offline', tone: 'warning', live: false };
  const label = MODE_LABELS[config.mode || '']?.label || config.mode || 'Unknown';
  // Continuous is the "nothing to think about" state; the triggered modes are worth a
  // second look when footage is missing, so they read as informational rather than green.
  return { label, tone: config.mode === 'continuous' ? 'success' : 'info', live: true };
}

/**
 * The recordings picker's camera badge: recording mode as the LABEL, camera connection as the
 * COLOUR. One chip carries both, so the list needs no separate status dot beside it.
 *
 * Deliberately different from `recordingState`, which answers "is a pipeline running right
 * now" and says "Camera offline" outright. Here the mode is the thing being asked for — what
 * will I find in this camera's timeline? — and the connection state only tints it, because a
 * camera that is down this minute still has yesterday's footage to review.
 *
 * Offline tints red whatever the mode, including "Off". A disabled camera that is also
 * unreachable is still unreachable, and swallowing that would make the colour mean "sometimes
 * status, sometimes not". The returned `title` spells the pair out for the tooltip.
 */
export function recordingModeBadge(
  config: { enabled?: boolean; mode?: string } | null | undefined,
  cameraOffline = false,
): RecordingState & { title: string } {
  const state = recordingState(config);
  // Still loading — there is no mode to tint yet, and no claim worth making about status.
  if (!config) return { ...state, title: state.label };
  if (cameraOffline) {
    return { ...state, tone: 'danger', live: false, title: `Recording: ${state.label} · camera offline` };
  }
  return { ...state, title: `Recording: ${state.label}` };
}

export const EVENT_TYPE_LABELS: Record<string, string> = {
  person: 'Person',
  person_count: 'People counting',
  intrusion: 'Intrusion',
  face: 'Face recognition',
  vehicle: 'Vehicle',
  ppe: 'PPE violation',
  theft: 'Theft',
  fall: 'Fall detection',
  fire_smoke: 'Fire & smoke',
  ocr: 'Text / plate reads (OCR)',
  other: 'Other detections',
};

/**
 * Categories that report on a TIMER rather than on activity, so selecting one means
 * "record continuously while it reports". People counting flushes roughly every 60s;
 * retained blocks are 5 minutes, so it will keep essentially everything.
 *
 * These are excluded from the "nothing selected = everything" default (the recorder
 * enforces that), and flagged in the UI so the choice is informed rather than a surprise.
 */
export const TIMER_DRIVEN_EVENT_TYPES = new Set(['person_count']);

/**
 * Sensitivity 1–10 → the score a scene must reach before it counts as motion.
 *
 * MIRRORS `threshold_for()` in ingest-recording's motion_detector.py, and must track it.
 * The recorder's own value (served with the live score) is authoritative and is what the
 * meter displays; this exists so the panel can show what a slider position WILL mean before
 * the change is saved, which is the only moment the operator can still reconsider.
 *
 * The score is the current frame's size over the scene's quiet baseline, so it reads as a
 * multiplier: a still room sits near 1.0 whatever the camera or bitrate.
 */
export const MOTION_THRESHOLD_MIN = 1.35;
export const MOTION_THRESHOLD_MAX = 4.0;

export function motionThresholdFor(sensitivity: number): number {
  const s = Math.max(1, Math.min(10, Math.round(Number(sensitivity) || 5)));
  return MOTION_THRESHOLD_MAX - ((MOTION_THRESHOLD_MAX - MOTION_THRESHOLD_MIN) * (s - 1)) / 9;
}

/** Plain-language reading of what a given threshold will and won't catch. */
export function motionSensitivityMeaning(sensitivity: number): string {
  const s = Math.max(1, Math.min(10, Math.round(Number(sensitivity) || 5)));
  if (s <= 2) return 'Only large, close movement — someone walking right past the lens.';
  if (s <= 4) return 'Strong movement across the frame. A distant or partly-visible person may not register.';
  if (s <= 6) return 'Ordinary room activity — a person moving through the scene.';
  if (s <= 8) return 'Subtle movement, further from the camera. Expect the occasional clip from lighting shifts.';
  return 'Almost any change, including camera noise, auto-exposure and shadows. Expect frequent clips.';
}

export const MOTION_SOURCE_LABELS: Record<string, string> = {
  auto: 'Auto (camera → stream)',
  onvif: 'Camera (ONVIF events)',
  bitrate: 'Stream (bitrate analysis)',
  pixel: 'Stream (pixel motion)',
};

/** Does this mode consult AI detections? */
export const modeUsesEvents = (mode: string) => mode === 'event' || mode === 'event_motion';
/** Does this mode consult motion? */
export const modeUsesMotion = (mode: string) => mode === 'motion' || mode === 'event_motion';

/** One-line explanation per field, shown under the input it describes. */
export const FIELD_HINTS = {
  enabled:
    'Off stops the recording pipeline entirely — no footage is written and playback has nothing to show.',
  event_types:
    'Which detections keep footage. Leave all unticked to accept every activity-based detection.',
  min_confidence:
    'Detections the AI is less sure about than this are ignored. 0 accepts everything; raise it if false detections are filling the disk.',
  motion_source:
    'Where motion comes from. Auto prefers the camera’s own ONVIF events and falls back to analysing the stream.',
  motion_sensitivity:
    'Sets the bar the live score below must clear. The score compares the picture against its own '
    + 'quiet baseline, so a still scene sits near 1.0 and bigger movement scores higher. Raising '
    + 'sensitivity LOWERS the bar: it catches subtler movement, and more wind, rain and lighting shifts with it.',
  motion_min_duration:
    'Movement must last at least this long to trigger. Filters out single-frame flickers.',
  motion_idle_timeout:
    'How long the scene must stay quiet before motion is considered over.',
  pre_roll:
    'Seconds kept BEFORE the trigger, so the clip shows the approach and not just the arrival.',
  post_roll:
    'Seconds kept AFTER the trigger. Each new trigger extends it again, so recording continues while activity does and stops this long after it ends.',
  max_clip:
    'Hard ceiling on one clip. On a permanently busy camera the window keeps the newest footage and drops the oldest past this limit.',
  retention_days:
    'How long footage is kept before automatic deletion. Blank uses the system default.',
} as const;
